import { makeAutoObservable, runInAction } from "mobx";
import api from "../Utils/api";

class PlaylistStore {
  loaded = false;

  pushingSong = false;

  playlist = [];

  constructor() {
    makeAutoObservable(this);
  }

  onLoadPlaylistHandler = null;

  preview = false;

  addSong(query, playlist, isPublic) {
    this.pushingSong = true;
    return api.pushSong(query, playlist, isPublic).finally(() => {
      runInAction(() => {
        this.pushingSong = false;
      });
    });
  }

  clear() {
    this.playlist = [];
    this.loaded = false;
    this.preview = false;
    this.onLoadPlaylistHandler = null;
  }

  onLoadPlaylist(handler) {
    this.onLoadPlaylistHandler = handler;
  }

  loadPlaylist(playlistId = undefined) {
    return api.getSongList(playlistId).then(({ list }) => {
      runInAction(() => {
        this.loaded = true;
        this.playlist = list;
        this.preview = true;
        // Use 'public' for virtual public playlist, otherwise use the actual playlistId
        this.currentViewingPlaylistId = playlistId || 'public';
        if (this.onLoadPlaylistHandler) this.onLoadPlaylistHandler(list);
      });
    });
  }

  loadUserPlayList(userId) {
    return api.getUserSongs(userId).then(({ list }) => {
      runInAction(() => {
        this.loaded = true;
        this.playlist = list;
        this.preview = true;
        this.currentViewingPlaylistId = 'my-songs';
      });
    });
  }

  userPlaylists = [];

  // Modal state management
  editingPlaylist = null;
  modalState = {
    create: false,
    edit: false,
    delete: false,
    share: false,
    addToPlaylist: false,
    addSong: false,
  };
  deleteTarget = null;

  // Sharing state
  sharingPlaylist = null;
  currentShares = [];
  mutualFriends = [];

  // Add to playlist state
  addingToPlaylistSong = null;
  addToPlaylistCurrentPlaylist = null;
  currentViewingPlaylistId = null;

  loadUserPlaylists() {
    return api.getUserPlaylists().then(({ list }) => {
      runInAction(() => {
        this.userPlaylists = list;
      });
    });
  }

  addPlaylist(title) {
    return api.addPlaylist(title).then((res) => {
      runInAction(() => {
        console.log(res);
        this.userPlaylists = [...this.userPlaylists, res];
      });
      return res;
    });
  }

  updatePlaylist(playlistId, data) {
    return api.updatePlaylist(playlistId, data).then((res) => {
      runInAction(() => {
        const index = this.userPlaylists.findIndex((p) => p.id === playlistId);
        if (index !== -1) {
          this.userPlaylists[index] = { ...this.userPlaylists[index], ...data };
        }
      });
      return res;
    });
  }

  deletePlaylist(playlistId) {
    return api.deletePlaylist(playlistId).then(() => {
      runInAction(() => {
        this.userPlaylists = this.userPlaylists.filter(
          (i) => i.id !== playlistId
        );
      });
    });
  }

  async quickPlayPlaylist(playlistId, PlaybackStore) {
    // Load the playlist data directly without using the handler
    const { list } = await api.getSongList(playlistId);

    if (!list || list.length === 0) {
      throw new Error('Playlist is empty');
    }

    // Directly set the playlist in PlaybackStore and start playback
    runInAction(() => {
      PlaybackStore.setPlaylist(list, playlistId);
      const firstTrackId = list[0].mediaId;
      // Use setTimeout to ensure playlist is fully set before playback
      setTimeout(() => {
        PlaybackStore.playTrack(firstTrackId);
      }, 50);
    });
  }

  async quickPlayPublicPlaylist(PlaybackStore) {
    // Load public playlist (no playlist ID)
    const { list } = await api.getSongList();

    if (!list || list.length === 0) {
      throw new Error('Public playlist is empty');
    }

    // Directly set the playlist in PlaybackStore and start playback
    runInAction(() => {
      PlaybackStore.setPlaylist(list, 'public');
      const firstTrackId = list[0].mediaId;
      setTimeout(() => {
        PlaybackStore.playTrack(firstTrackId);
      }, 50);
    });
  }

  async quickPlayUserSongs(userId, PlaybackStore) {
    // Load user's songs
    const { list } = await api.getUserSongs(userId);

    if (!list || list.length === 0) {
      throw new Error('Your songs playlist is empty');
    }

    // Directly set the playlist in PlaybackStore and start playback
    runInAction(() => {
      PlaybackStore.setPlaylist(list, 'my-songs');
      const firstTrackId = list[0].mediaId;
      setTimeout(() => {
        PlaybackStore.playTrack(firstTrackId);
      }, 50);
    });
  }

  // ========== Sharing Methods ==========

  async loadPlaylistShares(playlistId) {
    const { shares } = await api.getPlaylistShares(playlistId);
    runInAction(() => {
      this.currentShares = shares;
    });
  }

  async loadMutualFriends(playlistId) {
    const { friends } = await api.getMutualFriends(playlistId);
    runInAction(() => {
      this.mutualFriends = friends;
    });
  }

  async addShare(playlistId, sharedWithUserId, permission) {
    const { share } = await api.addPlaylistShare(playlistId, sharedWithUserId, permission);
    runInAction(() => {
      // Check if share already exists and update it, otherwise add new
      const existingIndex = this.currentShares.findIndex(
        s => s.sharedWithUserId === sharedWithUserId
      );
      if (existingIndex !== -1) {
        this.currentShares[existingIndex] = share;
      } else {
        this.currentShares.push(share);
      }
    });
    // Reload playlists to reflect changes
    await this.loadUserPlaylists();
    return share;
  }

  async updateShare(playlistId, shareId, permission) {
    await api.updatePlaylistShare(playlistId, shareId, permission);
    runInAction(() => {
      const index = this.currentShares.findIndex(s => s.id === shareId);
      if (index !== -1) {
        this.currentShares[index].permission = permission;
      }
    });
  }

  async removeShare(playlistId, shareId) {
    await api.removePlaylistShare(playlistId, shareId);
    runInAction(() => {
      this.currentShares = this.currentShares.filter(s => s.id !== shareId);
    });
    // Reload playlists to reflect changes
    await this.loadUserPlaylists();
  }

  openShareModal(playlist) {
    this.sharingPlaylist = playlist;
    this.modalState.share = true;
    this.loadPlaylistShares(playlist.id);
    this.loadMutualFriends(playlist.id);
  }

  closeShareModal() {
    this.sharingPlaylist = null;
    this.modalState.share = false;
    this.currentShares = [];
    this.mutualFriends = [];
  }

  // ========== Add to Playlist Methods ==========

  openAddToPlaylistModal(song, currentPlaylistId = null) {
    this.addingToPlaylistSong = song;
    this.addToPlaylistCurrentPlaylist = currentPlaylistId;
    this.modalState.addToPlaylist = true;
  }

  closeAddToPlaylistModal() {
    this.addingToPlaylistSong = null;
    this.addToPlaylistCurrentPlaylist = null;
    this.modalState.addToPlaylist = false;
  }

  async toggleSongInPlaylist(playlistId, mediaId, isInPlaylist) {
    if (isInPlaylist) {
      await api.removeSongFromPlaylist(playlistId, mediaId);
    } else {
      await api.addSongToPlaylist(playlistId, mediaId);
    }

    if (playlistId === this.currentViewingPlaylistId) {
      await this.loadPlaylist(playlistId);
    }
  }
}

const playlistStore = new PlaylistStore();
export default playlistStore;
